import {
  Component,
  ElementRef,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { cloneDeep } from 'lodash';
import { environment } from 'src/environments/environment';
import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';
import { DataTableContentEditorComponent } from '../data-table-common/data-table-content-editor.component';
import {
  DataTableField,
  DataTableSnapshot,
  DEFAULT_DEFINABLE_FIELDS,
} from '../data-table-common/data-table.types';

export type LayoutTableSettingsTab = 'layout' | 'content-data';

type LayoutTableSettingsTabConfig = {
  id: LayoutTableSettingsTab;
  label: string;
};

type ScrollSpeed = 'slow' | 'medium' | 'fast';

@Component({
  selector: 'template-component-layout-table',
  templateUrl: './layout-table.component.html',
  styleUrls: ['./layout-table.component.scss'],
  standalone: false,
})
export class LayoutTableComponent {
  private static readonly DEFAULT_TEXT_COLOR = 'rgba(17, 24, 39, 1)';
  private static readonly DEFAULT_ACCENT_COLOR = 'rgba(0, 135, 90, 1)';
  private static readonly DEFAULT_ACCENT_COLOR_TABLE = 'rgba(57, 76, 96, 0.03)';
  private static readonly DEFAULT_BACKGROUND_COLOR = 'rgba(255, 255, 255, 0.8)';
  private static readonly DEFAULT_SLIDE_DURATION = 6;
  private static readonly DEFAULT_COLUMNS = 4;
  private static readonly DEFAULT_SCROLL_SPEED: ScrollSpeed = 'medium';
  private static readonly MIN_COLUMNS = 1;
  private static readonly MAX_COLUMNS = 10;

  readonly LayoutType = ComponentsService.LAYOUTS_MAP['rise-layout-table'];

  private static readonly DEFAULT_LAYOUT_ID =
    ComponentsService.LAYOUTS_MAP['rise-layout-table'].CARD_GRID;

  readonly settingsTabs: LayoutTableSettingsTabConfig[] = [
    { id: 'layout', label: 'Layout' },
    { id: 'content-data', label: 'Content Data' },
  ];

  readonly scrollSpeedOptions: { value: ScrollSpeed; label: string }[] = [
    { value: 'slow', label: 'Slow' },
    { value: 'medium', label: 'Medium' },
    { value: 'fast', label: 'Fast' },
  ];

  readonly layoutOrder = [
    this.LayoutType.CARD_GRID,
    this.LayoutType.SINGLE,
    this.LayoutType.TABLE,
  ];

  @ViewChildren('layoutOptionBtn') layoutOptionBtns: QueryList<ElementRef>;
  @ViewChild('contentEditor') contentEditor: DataTableContentEditorComponent;

  private componentLayout: Record<string, unknown>;
  private componentServices: Record<string, unknown>[];

  public componentId: string;
  public activeSettingsTab: LayoutTableSettingsTab = 'layout';
  public layout = LayoutTableComponent.DEFAULT_LAYOUT_ID;
  public columns = LayoutTableComponent.DEFAULT_COLUMNS;
  public scrollSpeed: ScrollSpeed = LayoutTableComponent.DEFAULT_SCROLL_SPEED;
  public slideDuration = LayoutTableComponent.DEFAULT_SLIDE_DURATION;
  public showTableHeaders = true;
  public textColor = LayoutTableComponent.DEFAULT_TEXT_COLOR;
  public accentColor = LayoutTableComponent.DEFAULT_ACCENT_COLOR;
  public backgroundColor = LayoutTableComponent.DEFAULT_BACKGROUND_COLOR;

  constructor(
    private elementRef: ElementRef,
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    private templateEditorUtils: TemplateEditorUtilsService,
  ) {
    this.componentsService.registerDirective({
      type: 'rise-layout-table',
      element: this.elementRef.nativeElement,
      show: (options?) => {
        if (this.componentsService.selected?.id) {
          this.componentId = this.componentsService.selected.id;
          this.activeSettingsTab = 'layout';
          this.load();
        }
      },
    });
  }

  load() {
    this.ensureLayoutAndServicesLoaded();

    const layoutAttributes =
      (this.componentLayout?.attributes as Record<string, unknown>) || {};

    this.layout =
      (this.componentLayout?.layoutId as string) ||
      LayoutTableComponent.DEFAULT_LAYOUT_ID;
    this.columns = this.normalizeColumns(layoutAttributes.columns);
    this.scrollSpeed = this.normalizeScrollSpeed(layoutAttributes.scrollSpeed);
    this.slideDuration = this.normalizeSlideDurationSeconds(
      layoutAttributes.slideDuration,
    );
    this.showTableHeaders =
      layoutAttributes.showTableHeaders !== undefined
        ? !!layoutAttributes.showTableHeaders
        : true;
    this.textColor =
      (layoutAttributes.textColor as string) ??
      LayoutTableComponent.DEFAULT_TEXT_COLOR;
    this.accentColor =
      (layoutAttributes.accentColor as string) ??
      this.getDefaultAccentColor(this.layout);
    this.backgroundColor =
      (layoutAttributes.backgroundColor as string) ??
      LayoutTableComponent.DEFAULT_BACKGROUND_COLOR;

    // if environment is not defined, then this is a new component
    // re-save layout and content service defaults including environment
    const env = this.attributeDataService.getAttributeData(
      this.componentId,
      'environment',
    );
    if (!env) {
      this.saveLayout();
      const defaultContentData: DataTableSnapshot = {
        fields: DEFAULT_DEFINABLE_FIELDS.map((field) => ({ ...field })),
        values: [],
        source: null,
        account: null,
        name: '',
        hideFirstRow: true,
      };
      this.saveContentData(defaultContentData);
    }
  }

  loadContentData = (): DataTableSnapshot => {
    this.ensureLayoutAndServicesLoaded();

    const serviceAttributes = this.getServiceAttributes();
    const fields = this.templateEditorUtils.safeParseJson(
      serviceAttributes.fields,
      [],
    ) as DataTableField[];
    const values = this.templateEditorUtils.safeParseJson(
      serviceAttributes.values,
      [],
    ) as any[][];
    const hideFirstRow = serviceAttributes.hideFirstRow;

    return {
      fields,
      values,
      source: (serviceAttributes.source as string) ?? null,
      account: (serviceAttributes.account as string) ?? null,
      name: (serviceAttributes.name as string) || '',
      hideFirstRow: hideFirstRow === undefined ? true : Boolean(hideFirstRow),
    };
  };

  saveLayout() {
    if (!this.componentLayout) {
      this.componentLayout = {};
    }

    this.slideDuration = this.normalizeSlideDurationSeconds(this.slideDuration);

    this.componentLayout.type = this.componentLayout.type || 'file';
    this.componentLayout.layoutId = this.layout;
    this.componentLayout.attributes = {
      columns: this.columns,
      scrollSpeed: this.scrollSpeed,
      slideDuration: this.slideDuration,
      showTableHeaders: this.showTableHeaders,
      textColor: this.textColor,
      accentColor: this.accentColor,
      backgroundColor: this.backgroundColor,
    };

    this.attributeDataService.setAttributeData(
      this.componentId,
      'layout',
      this.componentLayout,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'environment',
      environment.production ? 'prod' : 'test',
    );
  }

  saveContentData = (snapshot: DataTableSnapshot): void => {
    this.ensureLayoutAndServicesLoaded();

    if (
      !Array.isArray(this.componentServices) ||
      this.componentServices.length === 0
    ) {
      this.componentServices = [{}];
    }

    const firstService = this.componentServices[0] as Record<string, unknown>;
    firstService.type = 'component';
    firstService.source = 'rise-data-table';
    firstService.attributes = {
      ...(firstService.attributes as Record<string, unknown>),
      values: JSON.stringify(snapshot.values),
      fields: JSON.stringify(snapshot.fields),
      account: snapshot.account,
      source: snapshot.source,
      name: snapshot.name,
      hideFirstRow: snapshot.hideFirstRow,
    };

    this.attributeDataService.setAttributeData(
      this.componentId,
      'services',
      this.componentServices,
    );
  };

  private ensureLayoutAndServicesLoaded(): void {
    const defaultAttributes =
      this.componentsService.getSelectedDirective()?.defaultAttributes || {};
    const defaultLayout = cloneDeep(defaultAttributes?.layout || {});
    const defaultServices = cloneDeep(defaultAttributes?.services || []);

    const rawLayout = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'layout',
    );
    const rawServices = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'services',
    );

    this.componentLayout = this.templateEditorUtils.safeParseJson(
      rawLayout,
      defaultLayout,
    ) as Record<string, unknown>;
    this.componentServices = this.templateEditorUtils.safeParseJson(
      rawServices,
      defaultServices,
    ) as Record<string, unknown>[];

    if (
      !Array.isArray(this.componentServices) ||
      this.componentServices.length === 0
    ) {
      this.componentServices = [{}];
    }
  }

  private getServiceAttributes(): Record<string, unknown> {
    const firstService = this.componentServices[0] as Record<string, unknown>;
    return (firstService?.attributes as Record<string, unknown>) || {};
  }

  private normalizeColumns(raw: unknown): number {
    const columns = this.templateEditorUtils.intValueFor(
      raw,
      LayoutTableComponent.DEFAULT_COLUMNS,
    );

    return Math.min(
      LayoutTableComponent.MAX_COLUMNS,
      Math.max(LayoutTableComponent.MIN_COLUMNS, columns),
    );
  }

  private normalizeScrollSpeed(raw: unknown): ScrollSpeed {
    const value = typeof raw === 'string' ? raw : '';
    const match = this.scrollSpeedOptions.find(
      (option) => option.value === value,
    );

    return match?.value ?? LayoutTableComponent.DEFAULT_SCROLL_SPEED;
  }

  private normalizeSlideDurationSeconds(raw: unknown): number {
    const coerced = this.templateEditorUtils.intValueFor(
      raw,
      LayoutTableComponent.DEFAULT_SLIDE_DURATION,
    );

    return Math.max(1, coerced);
  }

  setLayout(layoutId: string): void {
    this.layout = layoutId;

    // reset accent color to match the layout
    this.accentColor = this.getDefaultAccentColor(layoutId);
    this.saveLayout();
  }

  private getDefaultAccentColor(layoutId: string): string {
    if (layoutId === this.LayoutType.TABLE) {
      return LayoutTableComponent.DEFAULT_ACCENT_COLOR_TABLE;
    }

    return LayoutTableComponent.DEFAULT_ACCENT_COLOR;
  }

  onLayoutKeydown(event: KeyboardEvent, layoutId: string): void {
    const idx = this.layoutOrder.indexOf(layoutId);
    let nextIdx: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIdx = (idx + 1) % this.layoutOrder.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIdx = (idx - 1 + this.layoutOrder.length) % this.layoutOrder.length;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = this.layoutOrder.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.setLayout(this.layoutOrder[nextIdx]);
    this.layoutOptionBtns.get(nextIdx)?.nativeElement.focus();
  }

  selectSettingsTab(tab: LayoutTableSettingsTab): void {
    this.activeSettingsTab = tab;

    if (tab === 'content-data') {
      setTimeout(() => {
        void this.contentEditor?.load();
      });
    }
  }

  getSettingsTabId(tab: LayoutTableSettingsTab): string {
    return `layout-table-tab-${tab}`;
  }

  getSettingsTabPanelId(tab: LayoutTableSettingsTab): string {
    return `layout-table-panel-${tab}`;
  }

  onSettingsTabKeydown(
    event: KeyboardEvent,
    tab: LayoutTableSettingsTab,
  ): void {
    const tabIds = this.settingsTabs.map((settingsTab) => settingsTab.id);
    const currentIndex = tabIds.indexOf(tab);
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = currentIndex <= 0 ? tabIds.length - 1 : currentIndex - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = currentIndex >= tabIds.length - 1 ? 0 : currentIndex + 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabIds.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = tabIds[nextIndex];
    this.selectSettingsTab(nextTab);

    const nextTabElement = this.elementRef.nativeElement.querySelector(
      `#${this.getSettingsTabId(nextTab)}`,
    ) as HTMLElement | null;
    nextTabElement?.focus();
  }
}
